import { useEffect } from "react";
import { useCart } from "../context/cartContext";
import {
  CartLineExtensionAmount,
  CartPricingFooter,
} from "./CartMoneyDisplay";
import { X } from "lucide-react";
import { Link } from "@remix-run/react";
import Button from "../components/theme/Button";

interface CartPopupProps {
  isVisible: boolean;
  onClose: () => void;
}

export function CartPopup({ isVisible, onClose }: CartPopupProps) {
  const { cartItems, totalItems } = useCart();

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000); // Auto-close after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed top-4 right-4 z-50 w-96 max-w-[calc(100vw-2rem)] animate-in slide-in-from-right duration-300">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <h3 className="font-semibold text-lg">
            Cart ({totalItems} {totalItems === 1 ? "item" : "items"})
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="max-h-96 overflow-y-auto">
          {cartItems.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              <p>Your cart is empty</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <img
                    src={item.image || "/images/placeholder.svg"} 
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded-md flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm truncate">{item.name}</h4>
                    {item.store_name && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {item.store_name}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-1 gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Qty: {item.quantity}
                      </span>
                      <CartLineExtensionAmount item={item} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <CartPricingFooter
              items={cartItems}
              className="mb-3"
              amountClassName="text-lg font-bold"
            />
            <Link to="/cart" onClick={onClose}>
              <Button mode="primary" className="w-full" size="sm">
                View Cart
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

